import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from './UserContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBook, faGlobe, faDownload, faTrash, faStar, faFileAlt } from '@fortawesome/free-solid-svg-icons';
import TextModalWithPDF from './TextModalWithPDF';
import { useNavigate } from 'react-router-dom';
import api from "../api/apiClient";

const ProductsPage = ({ setCurrentPdfUrl, setIsViewingPDF, setRenderedPages, setIsLoading }) => {
  const { currentUser } = useContext(UserContext);
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isTextModalOpen, setTextModalOpen] = useState(false);
  const [currentTextContent, setCurrentTextContent] = useState("");
  const [currentProduct, setCurrentProduct] = useState(null);
  const [modalImage, setModalImage] = useState(null);
  const navigate = useNavigate();
  
  const handleCreateBook = () => {
    navigate('/skapa');
  };

  const openTextModal = async (textUrl, product) => {
    try {
      const response = await api.get(textUrl, { responseType: 'text' });
      setCurrentTextContent(response.data);
      setCurrentProduct(product);
      setTextModalOpen(true);
    } catch (error) {
      console.error('Error fetching text file:', error);
      alert('Ett fel inträffade vid hämtning av textfil.');
    }
  };

  const closeTextModal = () => {
    setTextModalOpen(false);
    setCurrentTextContent("");
  };

  const fetchProducts = async () => {
    if (!currentUser?.id) {
      console.error("Användar-ID saknas");
      return;
    }
  
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get(`/user/stories?user_id=${currentUser.id}`);
      const validatedProducts = data.stories.map((product) => {
        const validImageUrls = Array.isArray(product.insida_image_urls)
          ? product.insida_image_urls
          : JSON.parse(product.insida_image_urls || '[]');
        const omslagImageUrl = product.omslag_image_url && product.omslag_image_url.startsWith('http')
          ? product.omslag_image_url
          : "/images/default-cover.jpg";
  
        return {
          ...product,
          image_urls: validImageUrls,
          audio_url: product.audio_url?.startsWith('https://') ? product.audio_url : null,
          pdf_url: product.pdf_url?.startsWith('https://') ? product.pdf_url : null,
          omslag_image_url: omslagImageUrl,
          isPublic: product.is_public
        };
      });
  
      setProducts(validatedProducts);
    } catch (err) {
      console.error('Fetch error:', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [currentUser]);

  const sanitizeTitle = (title) => {
    return (title || "Namnlös bok").replace(/_/g, ' ');
  };

  const togglePublic = async (productId) => {
    try {
      await api.post(
        `/toggle-public?product_id=${productId}&user_id=${currentUser.id}`
      );
      setProducts((prev) =>
        prev.map((p) =>
          p.id === productId ? { ...p, isPublic: !p.isPublic } : p
        )
      );
    } catch (err) {
      console.error('Fel vid uppdatering av publiceringsstatus:', err);
      alert('Ett fel inträffade vid ändring av publiceringsstatusen.');
    }
  };

  const deleteProduct = async (productId) => {
    if (!currentUser) return;
  
    try {
      await api.delete(`/delete-product/${productId}?user_id=${currentUser.id}`);
      setProducts((prev) => prev.filter((p) => p.id !== productId));
      alert('Produkten har raderats.');
    } catch (err) {
      console.error('Error deleting product:', err.message);
      alert('Ett fel inträffade vid radering av produkten.');
    }
  };
  
  const openDemoBook = (pdfUrl) => {
    if (typeof setCurrentPdfUrl === 'function') {
      setCurrentPdfUrl(pdfUrl);
      setIsViewingPDF(true);
    } else {
      console.error('setCurrentPdfUrl is not a function');
    }
  };

  const paginatedProducts = products.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const renderPagination = () => {
    const totalPages = Math.ceil(products.length / itemsPerPage);

    if (totalPages <= 1) return null;

    const renderPageNumbers = () => {
      const pageNumbers = [];
      const maxPagesToShow = 5;
      let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
      let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

      if (endPage - startPage + 1 < maxPagesToShow) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }

      if (startPage > 1) {
        pageNumbers.push(
          <span key="first" onClick={() => handlePageChange(1)} className="cursor-pointer mx-1 text-sm">1</span>
        );
        if (startPage > 2) {
          pageNumbers.push(<span key="first-ellipsis" className="mx-1 text-sm">...</span>);
        }
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(
          <span
            key={i}
            onClick={() => handlePageChange(i)}
            className={`cursor-pointer mx-1 text-sm ${
              currentPage === i ? 'font-bold text-blue-600' : 'text-gray-600'
            }`}
          >
            {i}
          </span>
        );
      }

      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pageNumbers.push(<span key="last-ellipsis" className="mx-1 text-sm">...</span>);
        }
        pageNumbers.push(
          <span key="last" onClick={() => handlePageChange(totalPages)} className="cursor-pointer mx-1 text-sm">{totalPages}</span>
        );
      }

      return pageNumbers;
    };

    return (
      <div className="flex justify-center items-center mt-4 text-sm">
        {currentPage > 1 && (
          <span
            onClick={() => handlePageChange(currentPage - 1)}
            className="cursor-pointer mr-2 text-gray-600"
          >
            &lt;
          </span>
        )}
        {renderPageNumbers()}
        {currentPage < totalPages && (
          <span
            onClick={() => handlePageChange(currentPage + 1)}
            className="cursor-pointer ml-2 text-gray-600"
          >
            &gt;
          </span>
        )}
      </div>
    );
  };

  return (
    <article className="w-full bg-gradient-to-b from-white to-warmWhite shadow rounded-lg p-6">
      <h1 className="text-5xl text-black text-center font-bold mb-4">Dina Böcker</h1>
      
      {/* TILLFÄLLIGT UNDERHÅLLSMEDDELANDE */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              <strong>Tillfälligt underhåll:</strong> Beställning av fysiska böcker är för tillfället avstängd medan vi uppgraderar systemet. 
              Du kan fortfarande se och ladda ner dina digitala böcker nedan.
            </p>
          </div>
        </div>
      </div>

      {loading && <p>Hämtar produkter...</p>}
      {error && <p className="text-red-500">{error}</p>}
      {!loading && !error && products.length === 0 && (
        <div className="bg-gray-50 p-6 rounded shadow text-center">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Hoppsan! 🌟</h2>
          <p className="text-gray-700 mb-6">
            Det verkar som att du inte har några böcker ännu. Men oroa dig inte, du kan skapa din första bok direkt!
          </p>
          <button
            onClick={handleCreateBook}
            className="bg-turquoise text-white px-6 py-3 rounded shadow-lg hover:bg-turquoise transform transition-transform hover:scale-105"
          >
            <span className="flex items-center justify-center space-x-2">
              <FontAwesomeIcon icon={faStar} className="text-softYellow" />
              <span>Skapa en bok</span>
            </span>
          </button>
        </div>
      )}
      
      {isTextModalOpen && currentProduct && (
        <TextModalWithPDF
          isOpen={isTextModalOpen}
          currentProduct={currentProduct}
          currentTextContent={currentTextContent}
          closeTextModal={closeTextModal}
        />
      )}
      
      {modalImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setModalImage(null)}
        >
          <div
            className="bg-white p-4 rounded-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <img src={modalImage} alt="Förstorad bild" className="max-w-full" style={{ maxHeight: '70vh' }} />
            <button
              onClick={() => setModalImage(null)}
              className="mt-4 bg-red-500 text-white px-4 py-2 rounded"
            >
              Stäng
            </button>
          </div>
        </div>
      )}
      
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {paginatedProducts.map((product) => (
          <div
            key={product.id}
            className="border bg-white rounded-lg p-4 flex flex-col justify-between"
            style={{
              boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.06)",
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <div className="flex-grow">
              <div className="mb-4">
                <h2 className="text-xl font-semibold">{sanitizeTitle(product.name)}</h2>
              </div>
              {product.omslag_image_url && (
                <div className="mb-4">
                  <img
                    src={product.omslag_image_url}
                    alt="Omslagsbild"
                    className="w-full h-auto object-cover rounded border border-gray-300 shadow-sm cursor-pointer"
                    style={{
                      boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.4)",
                    }}
                    onClick={() => setModalImage(product.omslag_image_url)}
                  />
                </div>
              )}
              {product.summary && (
                <div className="mb-4">
                  <h3 className="font-semibold text-gray-800">Sammanfattning:</h3>
                  <p className="text-gray-600">{product.summary}</p>
                </div>
              )}
              {product.audio_url && (
                <div className="mb-4 w-full">
                  <audio controls className="w-full">
                    <source src={product.audio_url} type="audio/mpeg" />
                    Din webbläsare stödjer inte ljuduppspelning.
                  </audio>
                </div>
              )}
            </div>
            <div className="flex justify-center space-x-2 mt-auto">
              {product.pdf_url && (
                <button
                  onClick={() => openDemoBook(product.pdf_url)}
                  className="bg-red-500 text-white w-12 h-12 rounded-full flex items-center justify-center hover:bg-blue-600"
                  title="Öppna PDF"
                >
                  <FontAwesomeIcon icon={faBook} />
                </button>
              )}
              <button
                onClick={() => togglePublic(product.id)}
                className={`w-12 h-12 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors ${
                  product.isPublic ? 'bg-green-400 text-white' : 'bg-gray-300 text-black'
                }`}
                title={product.isPublic ? "Gör Privat" : "Gör Publik"}
              >
                <FontAwesomeIcon icon={faGlobe} />
              </button>
              {product.text_url && (
                <button
                  onClick={() => openTextModal(product.text_url, product)}
                  className="bg-lavenderPurple text-softYellow w-12 h-12 rounded-full flex items-center justify-center hover:bg-[#8A49A1]"
                  title="Öppna Text"
                >
                  <FontAwesomeIcon icon={faFileAlt} />
                </button>
              )}
              {product.audio_url && (
                <a
                  href={product.audio_url}
                  download
                  className="bg-turquoise text-warmWhite w-12 h-12 rounded-full flex items-center justify-center hover:bg-[#169E85]"
                  title="Ladda ner Ljudbok"
                >
                  <FontAwesomeIcon icon={faDownload} />
                </a>
              )}
              <button
                onClick={() => deleteProduct(product.id)}
                className="bg-lightCoralPink text-warmWhite w-12 h-12 rounded-full flex items-center justify-center hover:bg-[#E36496]"
                title="Radera"
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
              
              {/* BESTÄLLNINGSKNAPP BORTTAGEN - TILLFÄLLIGT INAKTIVERAD */}
              {/* 
              <button
                onClick={() => orderPhysicalBook(product.id)}
                className="bg-orange-500 text-white w-12 h-12 rounded-full flex items-center justify-center hover:bg-orange-600 opacity-50 cursor-not-allowed"
                title="Beställ fysisk bok (Tillfälligt avstängd)"
                disabled
              >
                <FontAwesomeIcon icon={faShoppingCart} />
              </button>
              */}
            </div>
          </div>
        ))}
      </section>
      {renderPagination()}
    </article>
  );
};

export default ProductsPage;